import React, { useEffect, useMemo, useState } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { DataGrid } from '@mui/x-data-grid';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import ContactsOutlinedIcon from '@mui/icons-material/ContactsOutlined';
import ForumOutlinedIcon from '@mui/icons-material/ForumOutlined';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import ViewColumnOutlinedIcon from '@mui/icons-material/ViewColumnOutlined';

import PageLayout from '../../../components/dashboard/PageLayout';
import PageHeader from '../../../components/dashboard/PageHeader';
import DataGridCard from '../../../components/dashboard/DataGridCard';
import ListFilterToolbar from '../../../components/dashboard/ListFilterToolbar';
import LoadingState from '../../../components/dashboard/LoadingState';
import PermissionDenied from '../../../components/dashboard/PermissionDenied';
import InitialFilterBar from '../../../components/dashboard/InitialFilterBar';
import SortSelect from '../../../components/dashboard/SortSelect';
import ViewModeToggle from '../../../components/dashboard/ViewModeToggle';
import useViewMode from '../../../components/dashboard/useViewMode';
import ContactCardGroups from '../../../components/contacts/ContactCardGroups';
import { useContactsInfiniteQuery, useContactsQuery } from '../../../components/contacts/useContactQueries';
import { buildContactColumns, contactLeadsPath, contactMenuActions, contactPath } from './columns';

const SEARCH_DEBOUNCE_MS = 400;
const INITIAL_PAGINATION = { page: 0, pageSize: 25 };
// Orderings offered in the card view, as DataGrid sort models: the grid and
// the cards share the same sort state, so switching views keeps the order.
// Only the API's ordering_fields (ContactViewSet).
const SORT_OPTIONS = {
  name: [{ field: 'display_name', sort: 'asc' }],
  nameDesc: [{ field: 'display_name', sort: 'desc' }],
  recent: [{ field: 'last_message_at', sort: 'desc' }],
  newest: [{ field: 'created_at', sort: 'desc' }],
};
const DEFAULT_SORT = SORT_OPTIONS.name;

// Sort model → option key. An empty model is the API default (by name).
const sortKey = (model) => {
  const [first] = model.length > 0 ? model : DEFAULT_SORT;
  return Object.keys(SORT_OPTIONS).find((key) => {
    const [option] = SORT_OPTIONS[key];
    return option.field === first.field && option.sort === first.sort;
  }) ?? '';
};

// Status filter → API params (ContactFilterSet). "Assigned to me" already
// implies an active conversation.
const STATUS_FILTERS = {
  active: { has_active_conversation: true },
  mine: { assigned_to_me: true },
};

/**
 * Contacts (end users who wrote to the customer on WhatsApp, created by the
 * first message), read-only. Two views of the same filters: cards grouped
 * by initial with infinite scroll (the default, and always below `md`) and a
 * server-side DataGrid. Which contacts show depends on the role (contact.view_all),
 * and phone/e-mail come masked without contact.view_sensitive.
 */
export default function ContactsList() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { viewMode, setViewMode, canToggle } = useViewMode('contacts', 'cards');
  const isCards = viewMode === 'cards';

  const [paginationModel, setPaginationModel] = useState(INITIAL_PAGINATION);
  const [sortModel, setSortModel] = useState(DEFAULT_SORT);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [status, setStatus] = useState('');
  const [initial, setInitial] = useState('');
  const [showColumnBorder, setShowColumnBorder] = useState(false);
  const [showCellBorder, setShowCellBorder] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [search]);

  const resetPage = () => setPaginationModel((prev) => ({ ...prev, page: 0 }));
  const updateSearch = (value) => {
    setSearch(value);
    resetPage();
  };
  const updateStatus = (value) => {
    setStatus(value);
    resetPage();
  };
  const updateInitial = (value) => {
    setInitial(value);
    resetPage();
  };
  const clearFilters = () => {
    setSearch('');
    setDebouncedSearch('');
    updateStatus('');
    updateInitial('');
  };

  const filterModel = {
    items: Object.entries({
      ...STATUS_FILTERS[status],
      // First letter of display_name, accents included; `#` = no letter (ContactFilterSet).
      ...(initial && { initial }),
    }).map(([field, value]) => ({ field, operator: '=', value })),
  };
  const gridQuery = useContactsQuery(
    { paginationModel, sortModel, search: debouncedSearch, filterModel },
    { enabled: !isCards },
  );
  const cardsQuery = useContactsInfiniteQuery(
    { sortModel, search: debouncedSearch, filterModel },
    { enabled: isCards },
  );
  const activeQuery = isCards ? cardsQuery : gridQuery;
  const currentSort = sortKey(sortModel);
  // Letter sections only make sense while the list is sorted by name.
  const groupedByInitial = sortModel.length === 0 || sortModel[0].field === 'display_name';
  const updateSort = (key) => {
    setSortModel(SORT_OPTIONS[key]);
    resetPage();
  };

  const contacts = gridQuery.data?.results ?? [];
  const cardContacts = useMemo(
    () => cardsQuery.data?.pages.flatMap((page) => page.results) ?? [],
    [cardsQuery.data],
  );
  const count = (isCards ? cardsQuery.data?.pages[0]?.count : gridQuery.data?.count) ?? 0;
  const columns = useMemo(
    () => buildContactColumns({ t, language: i18n.language, navigate }),
    [t, i18n.language, navigate],
  );

  const chips = [];
  if (status) {
    chips.push({
      key: 'status',
      label: `${t('contacts.filters.status')}: ${t(`contacts.filters.statusOptions.${status}`)}`,
      onDelete: () => updateStatus(''),
    });
  }
  if (initial) {
    chips.push({
      key: 'initial',
      label: `${t('contacts.filters.initial')}: ${initial}`,
      onDelete: () => updateInitial(''),
    });
  }
  if (search) {
    chips.push({
      key: 'search',
      label: `${t('contacts.filters.searchChip')}: ${search}`,
      onDelete: () => updateSearch(''),
    });
  }

  // `ListFilterToolbar` is passed to `slots.toolbar` as a stable reference;
  // everything dynamic goes through this plain props object.
  const toolbarProps = {
    filters: [
      {
        field: 'status',
        label: t('contacts.filters.status'),
        value: status,
        onChange: updateStatus,
        options: Object.keys(STATUS_FILTERS).map((value) => ({
          value,
          label: t(`contacts.filters.statusOptions.${value}`),
        })),
      },
    ],
    search: { label: t('contacts.filters.search'), value: search, onChange: updateSearch },
    chips,
    resultsLabel: t('contacts.filters.resultsFound', { count }),
    clearLabel: t('contacts.filters.clear'),
    onClearAll: clearFilters,
    endAction: canToggle && <ViewModeToggle value={viewMode} onChange={setViewMode} />,
    gridSettings: {
      showColumnBorder,
      onShowColumnBorderChange: setShowColumnBorder,
      showCellBorder,
      onShowCellBorderChange: setShowCellBorder,
    },
  };

  const header = (
    <PageHeader
      icon={<ContactsOutlinedIcon color="primary" />}
      title={t('contacts.title')}
      breadcrumbs={[
        { label: t('contacts.breadcrumb.dashboard') },
        { label: t('nav.groups.service') },
        { label: t('contacts.breadcrumb.list') },
      ]}
    />
  );

  if (activeQuery.isError && activeQuery.error?.response?.status === 403) {
    return (
      <PageLayout header={header}>
        <PermissionDenied
          title={t('contacts.states.permissionDeniedTitle')}
          message={t('contacts.states.listPermissionDeniedMessage')}
        />
      </PageLayout>
    );
  }

  const initialFilter = (
    <InitialFilterBar value={initial} onChange={updateInitial} label={t('contacts.filters.initial')} />
  );

  const cardProps = (contact) => ({
    to: contactPath(contact),
    menuActions: contactMenuActions({ t, navigate, contact }),
    actions: (
      <Stack direction="row" spacing={1} sx={{ width: '100%' }}>
        <Button variant="outlined" component={RouterLink} to={contactPath(contact)} sx={{ flex: 1 }}>
          {t('contacts.actions.details')}
        </Button>
        <Button
          variant="outlined"
          startIcon={<ForumOutlinedIcon />}
          component={RouterLink}
          to={contactLeadsPath(contact)}
          sx={{ flex: 1 }}
        >
          {t('contacts.actions.conversations')}
        </Button>
      </Stack>
    ),
  });

  return (
    <PageLayout header={header}>
      {isCards ? (
        // Cards go on the page background, not inside DataGridCard: in dark a
        // card inside another card blends with it. The toolbar keeps its card.
        <Stack spacing={2} sx={{ flex: 1, minHeight: 0, width: '100%' }}>
          <DataGridCard note={<FiltersNote t={t} />} sx={{ flex: '0 0 auto' }}>
            <ListFilterToolbar {...toolbarProps} showGridControls={false} />
          </DataGridCard>
          {initialFilter}
          {/* No pagination footer here: how many of the total are loaded so far. */}
          <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2} sx={{ px: 0.5 }}>
            <Typography variant="body2" color="text.secondary">
              {count > 0 && t('contacts.cards.showing', { shown: cardContacts.length, count })}
            </Typography>
            <SortSelect
              label={t('contacts.sort.label')}
              value={currentSort}
              onChange={updateSort}
              options={Object.keys(SORT_OPTIONS).map((key) => ({ value: key, label: t(`contacts.sort.${key}`) }))}
            />
          </Stack>
          <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
            {cardsQuery.isLoading && <LoadingState />}
            {!cardsQuery.isLoading && cardContacts.length === 0 && <EmptyState t={t} />}
            <ContactCardGroups
              contacts={cardContacts}
              grouped={groupedByInitial}
              getCardProps={cardProps}
              hasNextPage={cardsQuery.hasNextPage}
              isFetchingNextPage={cardsQuery.isFetchingNextPage}
              onFetchNextPage={cardsQuery.fetchNextPage}
              // Room for the card shadows inside the scroll area.
              sx={{ p: 0.5, pb: 2 }}
            />
          </Box>
        </Stack>
      ) : (
        // The grid's filters live inside its card, so the letters go right above it.
        <Stack spacing={2} sx={{ flex: 1, minHeight: 0, width: '100%' }}>
          {initialFilter}
          <DataGridCard note={<FiltersNote t={t} />}>
            <DataGrid
              // The name column has two lines (name + e-mail).
              getRowHeight={() => 'auto'}
              rows={contacts}
              columns={columns}
              getRowId={(row) => row.uuid}
              rowCount={count}
              loading={gridQuery.isLoading || gridQuery.isFetching}
              paginationMode="server"
              sortingMode="server"
              paginationModel={paginationModel}
              onPaginationModelChange={setPaginationModel}
              pageSizeOptions={[10, 25, 50]}
              sortModel={sortModel}
              onSortModelChange={(model) => {
                setSortModel(model);
                resetPage();
              }}
              // The API only filters by the toolbar; column filters would do nothing.
              disableColumnFilter
              disableRowSelectionOnClick
              showColumnVerticalBorder={showColumnBorder}
              slots={{
                toolbar: ListFilterToolbar,
                noRowsOverlay: EmptyState,
                columnSelectorIcon: ViewColumnOutlinedIcon,
              }}
              slotProps={{
                toolbar: toolbarProps,
                noRowsOverlay: { t },
                loadingOverlay: { variant: 'linear-progress', noRowsVariant: 'linear-progress' },
              }}
              sx={{
                border: 0,
                ...(showCellBorder && {
                  '& .MuiDataGrid-cell': { borderRight: '1px solid', borderColor: 'divider' },
                }),
              }}
            />
          </DataGridCard>
        </Stack>
      )}
    </PageLayout>
  );
}

function FiltersNote({ t }) {
  return (
    <Stack direction="row" spacing={1} alignItems="flex-start">
      <HelpOutlineIcon color="secondary" fontSize="small" />
      <Typography variant="caption" color="text.secondary">
        {t('contacts.filters.help.scope')}{' '}
        <Box component="span" sx={{ fontWeight: 'fontWeightBold' }}>
          {t('contacts.filters.status')}:
        </Box>{' '}
        {t('contacts.filters.help.status')}{' '}
        <Box component="span" sx={{ fontWeight: 'fontWeightBold' }}>
          {t('contacts.filters.searchChip')}:
        </Box>{' '}
        {t('contacts.filters.help.search')}
      </Typography>
    </Stack>
  );
}

function EmptyState({ t }) {
  return (
    <Stack alignItems="center" justifyContent="center" spacing={1} sx={{ py: 6, textAlign: 'center' }}>
      <Typography variant="body1">{t('contacts.list.empty')}</Typography>
      <Typography variant="body2" color="text.secondary">
        {t('contacts.list.emptyHint')}
      </Typography>
    </Stack>
  );
}
